import React from 'react';
import Card, { CardTitle } from '../../components/Card';
import Styles from './dashboardStyle.module.scss';
import ReactTooltip from 'react-tooltip';
import { FaInfoCircle } from 'react-icons/fa';
import { useNavigate } from 'react-router-dom';

// Mock Color Mapping for Health Types
const colorMapping: Record<string, string> = {
  NeedReboot: '#8B0A15',
  FailedUpdateAttempts: '#CF2929',
  DisconnectedFor30PlusDays: '#828282',
  NotCompatible: '#7D3434',
  // Add more mappings here as needed
};

const tooltipMapping: Record<string, string> = {
  NeedReboot: 'Devices that require a restart to complete updates.',
  FailedUpdateAttempts: 'Devices where updates failed in the last attempt.',
  DisconnectedFor30PlusDays: 'Devices that haven’t checked in for 30+ days.',
  NotCompatible: 'Devices not eligible for patching due to unsupported OS or architecture.',
  // Add more mappings here as needed
};

// Rotations for the HealthCard boxes
const rotationClasses = [
  { className: 'rotate-[180deg]', itemClass: 'rotate-[180deg]' },
  { className: 'rotate-[270deg]', itemClass: 'rotate-[90deg]' },
  { className: 'rotate-[90deg]', itemClass: 'rotate-[270deg]' },
  { className: 'rotate-[360deg]', itemClass: '' },
  // Add more if you expect more than 4 types
];

const MIN_BOX_SIZE = 60; // minimum size in px, so a 0-count box is still visible
const MAX_BOX_SIZE = 138; // maximum size in px, fits the 288px container in a 2x2 grid
const BASE_BOX_SIZE = (MIN_BOX_SIZE + MAX_BOX_SIZE) / 2; // equal size baseline when all values match

// Sizes each box relative to the average of all values: above-average values grow
// toward MAX_BOX_SIZE, below-average shrink toward MIN_BOX_SIZE. When every value
// is 0 (or all equal), every box renders at the equal BASE_BOX_SIZE.
const getBoxSizes = (values: number[]): number[] => {
  if (values.length === 0) return [];
  const average = values.reduce((sum, v) => sum + v, 0) / values.length;

  if (!average) return values.map(() => BASE_BOX_SIZE);

  return values.map((value) => {
    const scaled = Math.round(BASE_BOX_SIZE * (value / average));
    return Math.min(MAX_BOX_SIZE, Math.max(MIN_BOX_SIZE, scaled));
  });
};

// Component for rendering each square box
const HealthCard = ({ value = 0, size = BASE_BOX_SIZE, background = '#fff', className = '', itemClass = '' }) => {
  return (
    <div style={{ width: size, height: size }} className={`flex ${className}`}>
      <div
        style={{
          width: '100%',
          height: '100%',
          background: background,
        }}
        className={`flex items-center justify-center text-white rounded-[10px] ${itemClass}`}
      >
        {`${value} `}
      </div>
    </div>
  );
};

// Legend label with color box
type LegendProp = {
  label: string;
  color: string;
  displayName: string;
  groupId?: string;
};

const Legend = ({ label, color, displayName, groupId }: LegendProp) => {
  const navigate = useNavigate();
  let action;
  // console.log(displayName);

  switch (displayName) {
    case 'Needs Reboot':
      action = 'need-restart';
      break;
    case 'Failed Update Attempts':
      action = 'failedupdates';
      break;
    case 'Not Compatible':
      action = 'notcompatible';
      break;
    case 'Disconnected for 30+ days':
      action = 'disconnected';
      break;
    default:
      action = '';
  }

  const handleClick = () => {
    const groupParam = groupId ? `&groupId=${groupId}` : '';
    if (action == 'failedupdates') navigate(`/patchCenter/ActivityPatch?tab=patches&action=${action}${groupParam}`);
    else navigate(`/deviceManagement?action=${action}${groupParam}`);
  };

  return (
    <div className="flex items-center text-sm cursor-pointer hover:opacity-80" onClick={handleClick}>
      <span style={{ backgroundColor: color }} className="inline-block w-[12px] h-[12px] mr-[10px] rounded-[2px]"></span>
      {label}
    </div>
  );
};

// Main Device Health Component
const DeviceHealth = ({ deviceHealth = [], groupId = '' }) => {
  const hasData = deviceHealth.length > 0;

  return (
    <Card className={Styles['compliance-card ']}>
      <CardTitle title="Device Health" />
      {hasData ? (
        <div className="flex mt-6">
          {/* Legends */}
          <div className="flex flex-col justify-center gap-[20px]">
            {deviceHealth.map((task) => (
              <div key={task.healthType} className="flex items-center gap-2">
                <Legend
                  label={task.healthTypeDisplayName}
                  color={colorMapping[task.healthType] || '#999999'}
                  displayName={task.healthTypeDisplayName}
                  groupId={groupId}
                />
                <FaInfoCircle
                  data-tip
                  data-for={`tooltip-${task.healthType}`}
                  className="text-gray-400 hover:text-white cursor-pointer"
                  size={14}
                />
                <ReactTooltip
                  id={`tooltip-${task.healthType}`}
                  getContent={() => tooltipMapping[task.healthType] || 'No info available'}
                  className="!bg-gray-800 !text-white !text-xs !rounded"
                  place="right"
                  effect="solid"
                />
              </div>
            ))}
          </div>

          {/* Colored Square Cards */}
          <div className="w-[288px] h-[288px] grid grid-cols-2 grid-rows-2 gap-[10px] ml-6">
            {(() => {
              const values = deviceHealth.map((task) => Number(task.healthTypeValue) || 0);
              const sizes = getBoxSizes(values);
              // Anchor each quadrant's box toward the shared center point, so boxes of
              // different sizes still sit close together instead of each being centered
              // independently within its own cell.
              const cellAlignClasses = [
                'items-end justify-end',
                'items-end justify-start',
                'items-start justify-end',
                'items-start justify-start',
              ];

              return deviceHealth.map((task, index) => {
                const color = colorMapping[task.healthType] || '#999999';
                const rotation = rotationClasses[index % rotationClasses.length];

                return (
                  <div key={task.healthType} className={`flex ${cellAlignClasses[index % cellAlignClasses.length]}`}>
                    <HealthCard
                      value={values[index]}
                      size={sizes[index]}
                      background={color}
                      className={rotation.className}
                      itemClass={rotation.itemClass}
                    />
                  </div>
                );
              });
            })()}
          </div>
        </div>
      ) : (
        <div className="flex justify-center items-center h-[288px] text-gray-400 text-sm mt-6">No data available</div>
      )}
    </Card>
  );
};

export default DeviceHealth;
